import {skipToken} from '@reduxjs/toolkit/query';
import {Card} from 'platform/components';
import {Align, Heading, Hide, Link, Text} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import styled from 'styled-components';

import {useCallback, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';

import {isNotNil, sortBy} from 'ramda';

import {suffixTestId, TestIdProps, useNavigate} from 'shared/web';

import {getDefaultValuesWithResolvedAddress} from '../../../../components/FilterLayout/utils/getDefaultValuesWithResolvedAddress';
import {useFilterSummaryHelpers} from '../../../../components/SavedFiltersPanel/hooks/useFilterSummaryHelpers';
import {useCatalogue} from '../../../../hooks/useCatalogue';
import {useResolvedDefaultAddress} from '../../../../hooks/useResolvedDefaultAddress';
import {useSavedFiltersVisibility} from '../../../../hooks/useSavedFiltersVisibility';
import {useTrimLevelsCatalog} from '../../../../hooks/useTrimLevelsCatalog';
import i18n from '../../../../i18n/i18n';
import {useGetRelativeFiltersQuery} from '../../../../store/omneticApi';
import {RelativeFilterResponseBody} from '../../../../types/api/api';
import {PriceReportType} from '../../../../types/PriceReportType';
import {getStatisticsPath, QueryStateKeys} from '../../../../utils/routing';
import {useIsPrintForced} from '../../hooks/useIsPrintForced';
import {SavedFilterOverviewRow} from './components/SavedFilterOverviewRow';

/** Per-row offset between the card's statistics requests — ramp instead of burst. */
const CARD_QUERY_STAGGER_MS = 200;

const MAX_ROWS_BEFORE_TRUNCATION = 8;
const TRUNCATED_ROW_COUNT = 7;

interface SavedFiltersCardProps extends TestIdProps {
  priceReport: PriceReportType;
}

/**
 * Dashboard card with a compact market overview of every saved filter. Clicking a
 * row opens the Statistics page with that preset selected (deep link via ?presetId).
 * Rendered only when the report carries a tenant id — otherwise presets cannot be
 * fetched — and behind the saved-filters panel feature flag.
 */
export function SavedFiltersCard(props: SavedFiltersCardProps) {
  const {priceReport} = props;
  const {isPrint} = useIsPrintForced();
  const navigate = useNavigate();
  const params = useParams();
  const {customerId, showSavedFiltersPanel} = useSavedFiltersVisibility();
  const {hasFeatureTranslation} = useCatalogue();
  const formatNumber = useFormatNumber();
  const summaryHelpers = useFilterSummaryHelpers(priceReport);

  const {data: presets = []} = useGetRelativeFiltersQuery(
    showSavedFiltersPanel && isNotNil(customerId) ? {customerId} : skipToken
  );
  const orderedPresets = useMemo(() => sortBy((preset) => preset.position, presets), [presets]);

  // With more than 8 presets the card starts as 7 rows + a “Show more” link.
  // The PDF has no interaction, so it always renders every row.
  const [isShowingAll, setIsShowingAll] = useState(false);
  const isTruncated =
    !isPrint && orderedPresets.length > MAX_ROWS_BEFORE_TRUNCATION && !isShowingAll;
  const visiblePresets = isTruncated
    ? orderedPresets.slice(0, TRUNCATED_ROW_COUNT)
    : orderedPresets;

  // Overview has no filter form — presets resolve against the default filter values,
  // exactly what the Statistics page starts from after selecting the preset. The
  // default address needs resolved coordinates, otherwise preset radii would be
  // silently dropped from the metrics requests.
  const {resolvedAddress, isResolving: isAddressResolving} = useResolvedDefaultAddress(priceReport);
  const baseFormValues = useMemo(
    () => getDefaultValuesWithResolvedAddress(priceReport, resolvedAddress),
    [priceReport, resolvedAddress]
  );
  const {catalog: trimLevelsCatalog} = useTrimLevelsCatalog(
    priceReport,
    baseFormValues.firstRegistrationOnYearGte,
    baseFormValues.firstRegistrationOnYearLte
  );

  const handleOpenPreset = useCallback(
    (preset: RelativeFilterResponseBody) => {
      const [pathname, search = ''] = getStatisticsPath(params).split('?');
      const searchParams = new URLSearchParams(search);
      // set() replaces any stale presetId inherited from the current URL
      searchParams.set(QueryStateKeys.FilterPreset, preset.id);
      navigate(`${pathname}?${searchParams.toString()}`);
    },
    [navigate, params]
  );

  if (!showSavedFiltersPanel || orderedPresets.length === 0) {
    return null;
  }

  return (
    <Card data-testid={suffixTestId('savedFiltersCard', props)}>
      <Align center>
        <Heading size={3} data-testid={suffixTestId('savedFiltersCardTitle', props)}>
          {i18n.t('entity.filterForm.savedFiltersPanel.overview.title')}
        </Heading>
      </Align>
      <CardGrid>
        {/* Column headers only make sense in the aligned desktop grid — the stacked
            mobile rows carry their own inline labels instead. */}
        <Hide onMobile onTablet>
          <HeaderCell>
            <Text size="xSmall" color="tertiary" noWrap>
              {i18n.t('entity.filterForm.savedFiltersPanel.overview.filterColumn')}
            </Text>
          </HeaderCell>
          <HeaderCell $span={3} $isGroupStart>
            <Text size="xSmall" color="tertiary" noWrap>
              {i18n.t('entity.filterForm.savedFiltersPanel.overview.positionColumn')}
            </Text>
          </HeaderCell>
          <HeaderCell $span={2} $align="end">
            <Text size="xSmall" color="tertiary" noWrap>
              {i18n.t('entity.filterForm.savedFiltersPanel.advertised')}
            </Text>
          </HeaderCell>
          <HeaderCell $span={2} $align="end">
            <Text size="xSmall" color="tertiary" noWrap>
              {i18n.t('entity.filterForm.savedFiltersPanel.sold90')}
            </Text>
          </HeaderCell>
          <HeaderCell $align="end">
            <Text size="xSmall" color="tertiary" noWrap>
              {i18n.t('entity.filterForm.savedFiltersPanel.overview.ratioColumn')}
            </Text>
          </HeaderCell>
        </Hide>

        {visiblePresets.map((preset, presetIndex) => (
          <SavedFilterOverviewRow
            key={preset.id}
            preset={preset}
            priceReport={priceReport}
            queryStartDelayMs={presetIndex * CARD_QUERY_STAGGER_MS}
            baseFormValues={baseFormValues}
            isBaseResolving={isAddressResolving}
            isPrint={isPrint}
            trimLevelsCatalog={trimLevelsCatalog ?? null}
            isKnownFeature={hasFeatureTranslation}
            summaryHelpers={summaryHelpers}
            formatNumber={formatNumber}
            onOpen={handleOpenPreset}
            data-testid={props['data-testid']}
          />
        ))}
        {isTruncated ? (
          <ShowMoreSlot>
            <Link
              size="small"
              title={i18n.t('entity.filterForm.savedFiltersPanel.overview.showMore')}
              onClick={() => setIsShowingAll(true)}
              data-testid={suffixTestId('savedFiltersShowMore', props)}
            />
          </ShowMoreSlot>
        ) : null}
      </CardGrid>
    </Card>
  );
}

const CardGrid = styled.div`
  display: grid;
  /* One content-sized track per value: name | bar, place, percent | count, Ø | count, Ø | ratio.
     The tight gap keeps values of one labelled column close together; the wide gaps
     between the columns come from margins on each group's first value. */
  grid-template-columns:
    minmax(${({theme}) => theme.getSize(30)}, 1fr)
    repeat(8, max-content);
  column-gap: ${({theme}) => theme.getSize(1)};
  align-items: center;
  min-width: 0;
  margin-top: ${({theme}) => theme.getSize(4)};

  /* Stacked layout: name + ratio badge in the first row, metrics rows below */
  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    grid-template-columns: minmax(0, 1fr) max-content;
    column-gap: ${({theme}) => theme.getSize(2)};
  }
`;

const ShowMoreSlot = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  padding: ${({theme}) => `${theme.getSize(2)} 0`};
`;

const HeaderCell = styled.div<{$align?: 'end'; $span?: number; $isGroupStart?: boolean}>`
  display: flex;
  justify-content: ${({$align}) => ($align === 'end' ? 'flex-end' : 'flex-start')};
  padding-bottom: ${({theme}) => theme.getSize(1)};
  grid-column: span ${({$span}) => $span ?? 1};
  /* Left-aligned group headers start at the same offset as the group's first value */
  margin-left: ${({theme, $isGroupStart}) => ($isGroupStart ? theme.getSize(8) : 0)};
`;
